import { useEffect, useRef, useState } from "react";
import styles from "./searchModalDesktop.module.scss";

import Input from "@Components/Input";
import { getSearchResult } from "./data/helper";
import SearchResultList from "./components/SearchResultList";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import SearchIcon from "@Icons/SearchIcon";
import { useHistory } from "hooks/useHistory";
import DefaultSuggestionsDesktop from "./components/DefaultSuggestionsDesktop";
import customHistoryPush from "@Utils/customHistoryPush";
import useTranslationHook from "hooks/useTranslationHook";
import DropDownLoader from "@Components/DropdownLoader";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import useIsIframe from "hooks/useIsIframe";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import config from "@Config/index";

const SEARCH_MARKET = config.IS_CHINA_APP ? "china" : "global";

interface SearchModalDesktopProps {
  /** `clicked_search_bar`'s category. Default `search_page`; HomeV3's scrolled header
   * passes `PAGE.PROPERTY` ("property_page"), as the bar it replaced sent (P1D-5530). */
  searchBarCategory?: string;
}

const SearchModalDesktop = ({ searchBarCategory = PAGE.SEARCH }: SearchModalDesktopProps) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any>([]);
  const [inputValue, setInputValue] = useState("");
  const [showSearchResult, setShowSearchResult] = useState(false);
  // The rotating "Search by City / University / Property" layer sits over the field. It
  // shows only while the field is empty AND unfocused, however focus arrived (a click
  // anywhere on the bar, the icon, Tab) and however the text went (typed away, ✕). It
  // used to hide only when the layer itself was clicked and never come back, so focusing
  // the field any other way left it painted over whatever was typed.
  const [isFocused, setIsFocused] = useState(false);
  // The query the current `items` answer, so "No results" shows only once THIS query has
  // been answered, never while it is still in flight.
  const [resultsFor, setResultsFor] = useState("");
  const history = useHistory();
  const {
    isWhitelabel,
    partner_uuid,
    isDemoSubdomain,
    whitelabelColors,
    isRestrictedSearchEnabled,
    partner_id,
  } = useWhitelabel();
  const { isIframe } = useIsIframe();
  const useJavaApi = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.USE_JAVA_API_SEARCH);

  const timeout: any = useRef();
  // Every request takes a number; only the latest one may write its results, so a slow
  // answer for "lon" can't land after, and overwrite, the answer for "lond".
  const requestId = useRef(0);
  // True while an IME is composing (Chinese pinyin, Japanese, Korean): the field holds
  // half-typed letters ("bei j") that aren't the query yet. The search waits for the
  // composed text (北京).
  const composing = useRef(false);
  // Spaces alone, or around the words, are not a query.
  const query = inputValue.trim();
  const inputLength = query.length;
  const modalRef = useRef<any>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  // Search UX analytics refs (PRD): count keystrokes and measure time-to-select
  // across a single search session. Reset when the input is cleared.
  const keystrokeCountRef = useRef(0);
  const firstQueryAtRef = useRef<number | null>(null);
  const handleInputValueChange = (e: any) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    const trimmed = e.trim();
    const value = trimmed.toLowerCase();
    setInputValue(e);
    // a new query means a new list: no row is highlighted until the arrows pick one
    setSelectedIndex(-1);
    if (composing.current) return;

    // Track effort: count every keystroke and stamp the start of the session.
    keystrokeCountRef.current += 1;
    if (firstQueryAtRef.current === null) {
      firstQueryAtRef.current = Date.now();
    }

    requestId.current += 1;
    const thisRequest = requestId.current;
    if (value.length > 1) {
      setLoading(true);
      timeout.current = setTimeout(async () => {
        try {
          // Search query entered: fire on every debounced search API request
          // (mirrors the /api/v0/homes/search autocomplete call below).
          gtagEventEmitter({
            action: "search_query_entered",
            category: PAGE.HOME,
            label: "search_bar",
            search_query: value,
            query_length: value.length,
            market: SEARCH_MARKET,
            use_java_api: useJavaApi,
          });

          const res: any = await getSearchResult(
            trimmed,
            isWhitelabel,
            isRestrictedSearchEnabled,
            partner_id,
          );
          if (thisRequest !== requestId.current) return;
          const results = res?.data?.data || [];
          setItems(results);
          setResultsFor(value);

          // Suggestions displayed: fire with the number of suggestions shown (PRD).
          if (results.length > 0) {
            gtagEventEmitter({
              action: "search_suggestions_displayed",
              category: PAGE.HOME,
              label: "search_bar",
              search_query: value,
              suggestion_count: results.length,
              market: SEARCH_MARKET,
              use_java_api: useJavaApi,
            });
          }
        } catch {
          // A failed lookup shows "No results" for this query rather than the last
          // query's suggestions under it.
          if (thisRequest === requestId.current) {
            setItems([]);
            setResultsFor(value);
          }
        } finally {
          if (thisRequest === requestId.current) setLoading(false);
        }
      }, 200);
    } else {
      setItems([]);
      setLoading(false);
      setResultsFor("");
      // Session reset when the query is cleared / too short to search.
      keystrokeCountRef.current = 0;
      firstQueryAtRef.current = null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        (event.type == "mousedown" &&
          modalRef.current &&
          !modalRef.current.contains(event.target)) ||
        (event.type == "keydown" && event.key === "Escape")
      ) {
        setShowSearchResult(false);
        setSelectedIndex(-1);
        document.getElementById("heroSectionSearchBar")?.blur();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleClickOutside);
    };
  }, []);

  const isSuggestionsVisible = inputLength === 0;

  const handleOnClick = (data: any) => {
    let redirectLink = `/places/${data?.type != "inventory" ? "search/" : ""}${
      data?.canonical_name
    }`;
    if (isWhitelabel) {
      redirectLink = convertToWhitelabelURL(redirectLink, isDemoSubdomain, partner_uuid);
    }
    // Search UX (PRD): rank of the picked suggestion (1-based), how many
    // suggestions were shown, time from first keystroke to selection, and
    // number of keystrokes it took to get here.
    const rankIndex = items?.findIndex(
      (item: any) => item?.canonical_name === data?.canonical_name,
    );
    const suggestionRank = rankIndex >= 0 ? rankIndex + 1 : null;
    const timeToSelectMs =
      firstQueryAtRef.current !== null ? Date.now() - firstQueryAtRef.current : null;

    gtagEventEmitter({
      action: "clicked_searched_result",
      category: PAGE.SEARCH,
      label: `${redirectLink}, ${suggestionRank ?? 1}, ${inputValue}`,
      canonical_name: data.canonical_name,
      search_query: inputValue,
      suggestion_rank: suggestionRank,
      suggestion_count: items?.length ?? 0,
      time_to_select_ms: timeToSelectMs,
      keystrokes_to_select: keystrokeCountRef.current,
      market: SEARCH_MARKET,
      use_java_api: useJavaApi,
    });

    // Reset the session trackers after a completed selection.
    keystrokeCountRef.current = 0;
    firstQueryAtRef.current = null;

    // For whitelabel in iframe, open inventory in new tab
    if (isWhitelabel && isIframe && data.type === "inventory") {
      window.open(redirectLink, "_blank", "noopener,noreferrer");
    } else {
      customHistoryPush(history, redirectLink);
    }
  };

  const getItemProps: any = ({ data }: { data: any }) => ({
    onClick: () => handleOnClick(data),
  });

  const handleKeyDown = (e: any) => {
    const listItemHeight = 73.29;
    // Enter opens a result for THIS query only: while it is still loading, the list on
    // hand is the previous query's, and its first row is not what was asked for.
    const answered = !loading && resultsFor === query.toLowerCase();
    if (e.key === "Enter" && answered && items && items?.length > 0) {
      if (selectedIndex < 0) {
        handleOnClick(items?.[0]);
      } else handleOnClick(items?.[selectedIndex]);
    }
    if (items?.length) {
      const container: any = document.getElementById(`searchresultUl`);
      if (e.key == "ArrowDown" && items.length > selectedIndex + 1) {
        setSelectedIndex((si) => si + 1);
        const itemVisible = isVisible(
          document.getElementById(`searchListItem${selectedIndex + 2}`),
          container,
        );
        if (!itemVisible) container?.scrollTo(0, (selectedIndex - 2) * listItemHeight);
      } else if (e.key == "ArrowUp" && selectedIndex > 0) {
        setSelectedIndex((si) => si - 1);
        const itemVisible = isVisible(
          document.getElementById(`searchListItem${selectedIndex - 1}`),
          container,
        );
        if (!itemVisible) container?.scrollTo(0, (selectedIndex - 1) * listItemHeight);
      }
    }
  };

  const isVisible = (ele: any, container: any) => {
    const eleTop = ele?.offsetTop;
    const eleBottom = eleTop + ele?.clientHeight;

    const containerTop = container?.scrollTop;
    const containerBottom = containerTop + container?.clientHeight;

    // The element is fully visible in the container
    return (
      (eleTop >= containerTop && eleBottom <= containerBottom) ||
      // Some part of the element is visible in the container
      (eleTop < containerTop && containerTop < eleBottom) ||
      (eleTop < containerBottom && containerBottom < eleBottom)
    );
  };

  const handleOnFocus = () => {
    setIsFocused(true);
    setShowSearchResult(true);
    gtagEventEmitter({
      action: "clicked_search_bar",
      category: searchBarCategory,
      label: "search_bar",
      use_java_api: useJavaApi,
    });
  };

  const renderSearchResult = () => (
    <>
      {inputLength < 2 && inputLength > 0 && items?.length === 0 && (
        <div className={styles.defTextContainer}>
          <i className={`icon-keyboard ${styles.keyboardIcon}`} />
          <div data-testid="search-message" className={styles.defText}>
            {t("You can find your accommodation more easily if you add")} {2 - inputLength}{" "}
            {t("more alphabet")}
            {2 - inputLength > 1 ? "s" : ""}
          </div>
        </div>
      )}
      {isSuggestionsVisible && <DefaultSuggestionsDesktop gtagEventCategory={PAGE.SEARCH} />}
      {!isSuggestionsVisible && loading && !(inputLength < 2) ? (
        <DropDownLoader />
      ) : (
        <SearchResultList selectedIndex={selectedIndex} items={items} getItemProps={getItemProps} />
      )}
      {/* Answered, and nothing matched: say so, rather than an empty white panel. */}
      {inputLength >= 2 && !loading && resultsFor === query.toLowerCase() && !items?.length && (
        <div className={styles.defTextContainer} data-testid="search-no-results">
          <div className={styles.defText}>
            {t("searchpage.leadformwrapper.leadformmodalv2.personaldetailsform.noresults")}
          </div>
        </div>
      )}
    </>
  );

  const { t } = useTranslationHook();
  const searchPlaceHolderText = t("home.searchinputhome.searchmodaldesktop.searchplaceholder");
  const searchText = t("home.searchinputhome.searchmodaldesktop.searchbyprefix");
  const cityText = t("home.searchinputhome.searchmodaldesktop.citysuffix");
  const universityText = t("home.searchinputhome.searchmodaldesktop.universitysuffix");
  const propertyText = t("home.searchinputhome.searchmodaldesktop.propertysuffix");
  const cityUnivOrPropText = t("home.searchinputhome.searchmodaldesktop.cityuniversityorproperty");

  return (
    <div className={formatClassNames(styles.desktop)} data-testid="desktop-search-section">
      <div
        className={styles["searchContainer"]}
        onCompositionStart={() => {
          composing.current = true;
        }}
        onCompositionEnd={(e) => {
          composing.current = false;
          handleInputValueChange((e.target as HTMLInputElement).value ?? "");
        }}
      >
        <Input
          type="text"
          label=""
          className={styles["searchInput"]}
          placeholder={searchPlaceHolderText}
          value={inputValue}
          onChange={handleInputValueChange}
          showClearInnput
          autoFocus={false}
          onFocus={() => handleOnFocus()}
          onBlur={() => setIsFocused(false)}
          showPlaceholder
          onKeyDown={handleKeyDown}
          isHomePageDesktop
          id="heroSectionSearchBar"
          dataTestId="heroSectionSearchBar"
        />
        {!inputValue && !isFocused && (
          <div
            className={styles.animateContainer}
            role="none"
            onClick={() => {
              document.getElementById("heroSectionSearchBar")?.focus();
            }}
          >
            <span className={styles.staticWord}>{searchText} </span>
            <div className={styles.animateWordsWrapper}>
              <span className={styles.animateWords}>{cityText}</span>
              <span className={styles.animateWords}>{universityText}</span>
              <span className={styles.animateWords}>{propertyText}</span>
              <span className={styles.animateWords}>{cityUnivOrPropText}</span>
            </div>
          </div>
        )}
        {!inputValue && (
          <i
            data-testid="search-icon"
            className={formatClassNames(
              styles.searchIcon,
              isWhitelabel ? styles.whitelabelSearchIcon : "",
            )}
            onClick={handleOnFocus}
            role="none"
          >
            <SearchIcon
              fill={isWhitelabel ? whitelabelColors?.primary_text_color : "#ffffff"}
              size="100%"
            />
          </i>
        )}
      </div>
      {showSearchResult && (
        <div className={styles.desktopModal} id="searchresultUl" ref={modalRef}>
          {renderSearchResult()}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(SearchModalDesktop, {
  componentName: "SearchModalDesktop-Global",
  showForChina: true,
});
